import { useEffect, useState } from "react";
import {
  GENRES,
  GENRE_LIST,
  numJP,
  type GenreKey,
  type Work,
} from "../data";
import type { Nav } from "../types";
import {
  Empty,
  Footer,
  LoadingBlock,
  RankItem,
  Stars,
  WorkCard,
  WorkThumb,
} from "../components/atoms";
import { useFetch } from "../hooks";
import { fetchNewWorks, fetchPickup, fetchRankingWithWorks, listWorks } from "../api";
import { useT } from "../i18n";

interface Props {
  nav: Nav;
}

export function HomePage({ nav }: Props) {
  const t = useT();
  const [activeGenre, setActiveGenre] = useState<"all" | GenreKey>("all");
  const [genreWorks, setGenreWorks] = useState<Work[] | null>(null);
  const [genreLoading, setGenreLoading] = useState(false);

  const pickup = useFetch(fetchPickup, []);
  const newWorks = useFetch(fetchNewWorks, []);
  const ranking = useFetch(fetchRankingWithWorks, []);

  useEffect(() => {
    setGenreLoading(true);
    listWorks({
      ...(activeGenre !== "all" ? { genres: [activeGenre] } : {}),
      limit: 10,
      sort: "popular",
    })
      .then((ws) => setGenreWorks(ws))
      .catch(() => setGenreWorks([]))
      .finally(() => setGenreLoading(false));
  }, [activeGenre]);

  return (
    <div className="page page-home">
      <div className="container">
        {/* HERO */}
        <section style={{ marginTop: 24 }}>
          <div className="hero">
            <div className="hero-image">
              {pickup.data ? (
                <WorkThumb
                  work={pickup.data}
                  caption={`KEY-VISUAL // ${pickup.data.title}`}
                  seed={4}
                />
              ) : (
                <LoadingBlock height={300} />
              )}
            </div>
            <div className="hero-body">
              <span className="pickup-tag">{t("home.pickup", { week: 19 })}</span>
              <h1>{pickup.data?.title ?? "—"}</h1>
              <div className="hero-author">
                {pickup.data?.authorName ?? ""} ·{" "}
                <span style={{ color: "var(--muted-2)" }}>
                  {pickup.data ? GENRES[pickup.data.genre]?.ja : ""}{" "}
                  {pickup.data ? `/ ${pickup.data.estimatedPlayTime ?? "?"}分` : ""}
                </span>
              </div>
              <div className="hero-desc">
                {pickup.data?.shortDescription}
                <br />
                <span style={{ color: "var(--muted-2)" }}>
                  {t("home.pickupSubtitle")}
                </span>
              </div>
              {pickup.data && (
                <div className="hero-meta">
                  <Stars rating={pickup.data.rating ?? 0} size={13} />
                  <span>{(pickup.data.rating ?? 0).toFixed(1)}</span>
                  <span>·</span>
                  <span>
                    {t("home.reviews", { count: numJP(pickup.data.reviews ?? 0) })}
                  </span>
                  <span>·</span>
                  <span>▶ {numJP(pickup.data.stats.plays)}</span>
                </div>
              )}
              <div className="hero-actions">
                <button
                  className="btn btn-accent"
                  onClick={() =>
                    pickup.data && nav({ page: "work", id: pickup.data.id })
                  }
                >
                  {t("home.playFree")}
                </button>
                <button
                  className="btn"
                  onClick={() =>
                    pickup.data && nav({ page: "work", id: pickup.data.id })
                  }
                >
                  {t("home.viewDetail")}
                </button>
                <button
                  className="btn btn-ghost btn-icon"
                  title={t("home.addLibrary")}
                >
                  ♡
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* NEW */}
        <section className="section">
          <div className="section-head">
            <h2>
              {t("home.new")} <span className="sh-en">{t("home.newEn")}</span>
            </h2>
            <a
              href="#"
              className="sh-more"
              onClick={(e) => {
                e.preventDefault();
                nav({ page: "search", sort: "new" });
              }}
            >
              {t("home.viewMore")}
            </a>
          </div>
          <div className="hscroll">
            {newWorks.data ? (
              newWorks.data.map((w, i) => (
                <WorkCard
                  key={w.id}
                  work={w}
                  onClick={(work) => nav({ page: "work", id: work.id })}
                  seed={i + 7}
                />
              ))
            ) : (
              <LoadingBlock height={180} />
            )}
          </div>
        </section>

        {/* RANKING */}
        <section className="section">
          <div className="section-head">
            <h2>
              {t("home.weeklyRanking")}{" "}
              <span className="sh-en">{t("home.weeklyRankingEn")}</span>
            </h2>
            <a
              href="#"
              className="sh-more"
              onClick={(e) => {
                e.preventDefault();
                nav({ page: "search", sort: "popular" });
              }}
            >
              {t("home.viewAll")}
            </a>
          </div>
          <div className="rank-list">
            {ranking.data ? (
              ranking.data.map(({ work, rank, prev }) => (
                <RankItem
                  key={work.id}
                  rank={{ rank, prev }}
                  work={work}
                  onClick={(w) => nav({ page: "work", id: w.id })}
                />
              ))
            ) : (
              <LoadingBlock height={120} />
            )}
          </div>
        </section>

        {/* GENRE */}
        <section className="section">
          <div className="section-head">
            <h2>
              {t("home.byGenre")} <span className="sh-en">{t("home.byGenreEn")}</span>
            </h2>
          </div>
          <div style={{ marginBottom: 16 }}>
            <div className="genre-tabs">
              {GENRE_LIST.map((g) => (
                <button
                  key={g.key}
                  className={`gtab ${activeGenre === g.key ? "active" : ""}`}
                  onClick={() => setActiveGenre(g.key)}
                >
                  {g.ja}
                </button>
              ))}
            </div>
          </div>
          {genreLoading ? (
            <LoadingBlock height={200} />
          ) : genreWorks && genreWorks.length > 0 ? (
            <div className="grid-5">
              {genreWorks.map((w, i) => (
                <WorkCard
                  key={w.id}
                  work={w}
                  onClick={(work) => nav({ page: "work", id: work.id })}
                  seed={i + 13}
                />
              ))}
            </div>
          ) : (
            <Empty
              title={t("home.emptyGenre")}
              desc={t("home.emptyGenreDesc")}
              action={<button className="btn btn-accent btn-sm">{t("home.studioCreate")}</button>}
            />
          )}
        </section>
      </div>
      <Footer />
    </div>
  );
}
